import StringMask from "string-mask";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { FIELD_MASK } from "@/utils/const";
import i18n, { i18nInstance } from "~~/plugins/i18n";

dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  months: [
    "Január",
    "Február",
    "Március",
    "Április",
    "Május",
    "Június",
    "Július",
    "Augusztus",
    "Szeptember",
    "Október",
    "November",
    "December",
  ],
  monthsShort: ["Jan", "Febr", "Már", "Ápr", "Máj", "Jún", "Júl", "Aug", "Szep", "Okt", "Nov", "Dec"],
});

export function formatSeconds(seconds) {
  if (!seconds) {
    return "0 perc";
  }
  let minutes = Math.floor(seconds / 60);
  let hours = Math.floor(minutes / 60);
  let days = Math.floor(hours / 24);
  minutes = minutes % 60;
  if (days >= 1) hours = hours % 24;
  return `${days ? days + " nap " : " "}${hours ? hours + " óra " : " "}${minutes ? minutes + " perc " : " "}`;
}

export function monify(number) {
  if (number === null || number === undefined) return 0;
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function formatDate(date) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).format("YYYY-MM-DD");
}

export function formatDateWithMonthName(date) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).format("YYYY. MMMM DD.").toLowerCase();
}

export function roundTwoDigits(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export function formatDateTime(date) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).format("YYYY. MMMM D. HH:mm").toLowerCase();
}

export function formatMonthDay(date) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).format("MMMM D.");
}

export function getDaysFromToday(date) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).hour(23).minute(59).diff(dayjs(new Date()), "day");
}

export function getMonthName(date) {
  return dayjs(date).format("MMMM");
}

export function getShortMonthName(date) {
  // return $d(date, "shortMonthName");
  return dayjs(date).format("MMM");
}

export function addDay(date, numberOfDays) {
  return dayjs(date).add(numberOfDays, "day").format("YYYY-MM-DD");
}

export function subtractDay(date, numberOfDays) {
  return dayjs(date).subtract(numberOfDays, "day").format("YYYY-MM-DD");
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

export function sortObjectKeys(obj) {
  const keys = Object.keys(obj);
  keys.sort();

  const sortedObj = {};
  for (const key of keys) {
    sortedObj[key] = obj[key];
  }

  return sortedObj;
}

export function generateNonce() {
  let result = [];
  let characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let charactersLength = characters.length;
  for (let i = 0; i < 16; i++) {
    result.push(characters.charAt(Math.floor(Math.random() * charactersLength)));
  }
  return result.join("");
}

export function maskValue(mask, value) {
  if (mask === FIELD_MASK.HUF) {
    if (value) {
      let valueForFormat = typeof value === "string" ? value.replace(/\D/g, "") : value;
      const monified = monify(valueForFormat);
      if (monified === 0) return "";
      return monified;
    }
  } else if (mask === FIELD_MASK.PHONE) {
    if (value) {
      let valueForFormat = typeof value === "string" ? value.replace(/^[+]?36/, "").replaceAll(" ", "") : value;
      let apply = new StringMask("+36 00 000 0000").apply(valueForFormat).trimEnd();
      return apply ? apply : "+36";
    } else {
      return "+36";
    }
  }
  return value;
}

export function formatPhoneNumber(mask, value) {
  return "+" + new StringMask(mask).apply(value).trim();
}

export function formatStepTime(seconds, locale) {
  const options = {
    locale,
    style: "unit",
    unitDisplay: "long",
  };
  const minutesFormatter = new Intl.NumberFormat(options.locale, {
    style: options.style,
    unit: "minute",
    unitDisplay: options.unitDisplay,
  });
  const secondsFormatter = new Intl.NumberFormat(options.locale, {
    style: options.style,
    unit: "second",
    unitDisplay: options.unitDisplay,
  });
  const calculatedMinutes = Math.floor(seconds / 60);
  const calculatedSeconds = seconds % 60;
  return calculatedMinutes
    ? calculatedSeconds
      ? `${minutesFormatter.format(calculatedMinutes)} ${secondsFormatter.format(calculatedSeconds)}`
      : `${minutesFormatter.format(calculatedMinutes)}`
    : `${secondsFormatter.format(calculatedSeconds)}`;
}

// Call this in mounted not in created
export async function resetValidator(observer) {
  await observer.validate();
  observer.reset();
}

export function translateOrderNumber(number, locale) {
  const ordinalRules = new Intl.PluralRules(locale, { type: "ordinal" });
  switch (number) {
    case 1:
      return i18nInstance.global.t("bnpl:first");
    case 2:
      return i18nInstance.global.t("bnpl:second");
    case 3:
      return i18nInstance.global.t("bnpl:third");
    case 4:
      return i18nInstance.global.t("bnpl:fourth");
    case 5:
      return i18nInstance.global.t("bnpl:fifth");
    case 6:
      return i18nInstance.global.t("bnpl:sixth");
    case 7:
      return i18nInstance.global.t("bnpl:seventh");
    case 8:
      return i18nInstance.global.t("bnpl:eighth");
    default:
      return i18nInstance.global.t(`bnpl:ordinal.${ordinalRules.select(number)}.prefix`) + ordinalRules.select(number) + i18nInstance.global.t(`bnpl:ordinal.${ordinalRules.select(number)}.suffix`);
  }
}

export function getTransactionIcon(transaction) {
  switch (transaction.status) {
    case "PAID":
      return "PAID";
    case "DELAYED":
      return "SAND_CLOCK";
    case "LATE":
      return "LATE";
    case "DUE":
      return "DUE";
    case "STARTED":
      return "SAND_CLOCK";
    default:
      return "DUE";
  }
}

export function validateEmail(email) {
  return String(email).match(/^\S+@\S+\.\S+$/);
}

export function formatDecimalSeparator(str) {
  if (str) {
    return String(str).replace(".", ",");
  }
  return "0";
}
